import { useState } from 'react';
import { Box, CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/AddCommentOutlined';
import ChatIcon from '@mui/icons-material/ForumOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import MoreVertIcon from '@mui/icons-material/MoreVertRounded';
import PlayArrowIcon from '@mui/icons-material/PlayArrowRounded';
import { useTranslation } from '../../i18n';
import type { Conversation } from '../../lib/agent';
import { useConversationThumb } from '../../lib/agentThumbs';
import { HistoryPanel, HistoryPanelMessage, HistoryPanelRow, HistoryThumb } from '../HistoryPanel';

// The agent page's chat history, drawn like the generator pages' "Recent"
// column: newest first, a thumbnail of each chat's latest generation (a
// spinner while one renders), the title and where the chat left off.

export const AGENT_HISTORY_WIDTH = 260;

function ChatRow({
  conv,
  active,
  running,
  onOpen,
  onDelete,
}: {
  conv: Conversation;
  active: boolean;
  running: boolean;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const t = useTranslation().videoAgent;
  const thumb = useConversationThumb(conv.latest_job, running);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <HistoryPanelRow selected={active} onClick={onOpen} alignItems="flex-start">
      <HistoryThumb>
        {running ? (
          <Tooltip title={t.rendering}>
            <CircularProgress size={16} aria-label={t.rendering} sx={{ color: 'grey.500' }} />
          </Tooltip>
        ) : thumb ? (
          <>
            <Box component="img" src={thumb} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            {conv.latest_job?.kind === 'video' && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'common.white' }}>
                <PlayArrowIcon sx={{ fontSize: 22, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.7))' }} />
              </Box>
            )}
          </>
        ) : (
          <ChatIcon sx={{ fontSize: 20, color: 'grey.600' }} />
        )}
      </HistoryThumb>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {conv.origin === 'api' && (
            <Box
              component="span"
              sx={{ flexShrink: 0, fontSize: 9.5, fontWeight: 700, lineHeight: 1.4, px: 0.5, borderRadius: 0.75, border: 1, borderColor: 'divider', color: 'text.secondary' }}
            >
              {t.apiRun}
            </Box>
          )}
          <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.35 }} noWrap>
            {conv.origin === 'api' ? conv.title.replace(/^API · /, '') : conv.title || t.untitled}
          </Typography>
        </Box>
        {conv.preview && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: 10.5,
              lineHeight: 1.35,
              mt: 0.25,
            }}
          >
            {conv.preview}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', lineHeight: 1.3, fontSize: 10, mt: 0.25 }} noWrap>
          {running
            ? t.rendering
            : new Date(conv.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>

      {/* An API run belongs to the API: it is not deleted from here. */}
      <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, mt: -0.25, visibility: conv.read_only ? 'hidden' : undefined }}>
        <IconButton size="small" aria-label={t.chatActions} onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
          <MoreVertIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDelete();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t.deleteChat} />
          </MenuItem>
        </Menu>
      </Box>
    </HistoryPanelRow>
  );
}

export function AgentHistoryPanel({
  conversations,
  loaded,
  activeId,
  runningIds,
  onOpen,
  onNew,
  onDelete,
  newDisabled,
  sticky = true,
  width = AGENT_HISTORY_WIDTH,
  sx,
}: {
  conversations: Conversation[];
  loaded: boolean;
  activeId: string | null;
  runningIds: Set<string | undefined>;
  onOpen: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  newDisabled?: boolean;
  sticky?: boolean;
  width?: number | string;
  sx?: Parameters<typeof HistoryPanel>[0]['sx'];
}) {
  const t = useTranslation().videoAgent;
  return (
    <HistoryPanel
      title={t.chats}
      width={width}
      sticky={sticky}
      sx={sx}
      action={
        <Tooltip title={t.newChat}>
          <span>
            <IconButton size="small" onClick={onNew} disabled={newDisabled} aria-label={t.newChat}>
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      {!loaded ? (
        <HistoryPanelMessage loading />
      ) : !conversations.length ? (
        <HistoryPanelMessage>{t.noChats}</HistoryPanelMessage>
      ) : (
        <Box sx={{ py: 0.5 }}>
          {conversations.map((c) => (
            <ChatRow
              key={c.id}
              conv={c}
              active={c.id === activeId}
              running={runningIds.has(c.id)}
              onOpen={() => onOpen(c.id)}
              onDelete={() => onDelete(c.id)}
            />
          ))}
        </Box>
      )}
    </HistoryPanel>
  );
}
